'use client'

import { cn } from '@/utilities/ui'
import { type VariantProps, cva } from 'class-variance-authority'
import * as React from 'react'

/**
 * NavCTA - Compact call-to-action button component specifically designed for navigation bars
 *
 * @description Specialized navigation button component optimized for header/navbar contexts.
 * Features compact sizing, clean styling without intrusive arrows, and theme-aware colors
 * that adapt to navigation background. Designed to integrate seamlessly with navigation
 * items while maintaining clear visual hierarchy for conversion actions.
 *
 * Features a split-flap/train board style text animation on hover where the text
 * appears to roll upward, revealing the same text from below.
 *
 * @example
 * // Outline variant for secondary navigation actions
 * <NavCTA variant="outline" href="/sales">Sales Inquiries</NavCTA>
 * <NavCTA variant="outline" navigationBackground="black">Contact Us</NavCTA>
 *
 * // Filled variant for primary navigation actions
 * <NavCTA variant="filled" href="/signup">Create Account</NavCTA>
 * <NavCTA variant="filled" navigationBackground="lime">Get Started</NavCTA>
 *
 * // Navigation with external links
 * <NavCTA variant="filled" href="https://app.civo.com" newTab>
 *   Launch Dashboard
 * </NavCTA>
 *
 * // Theme-aware navigation CTAs
 * <NavCTA variant="outline" navigationBackground="black">
 *   Learn More
 * </NavCTA>
 * <NavCTA variant="filled" navigationBackground="white">
 *   Sign Up Free
 * </NavCTA>
 */
const navCTAVariants = cva(
  'inline-flex items-center justify-center rounded-full font-["Hanken_Grotesk"] font-medium text-[16px] leading-5 tracking-[-0.7px] transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 group overflow-hidden',
  {
    variants: {
      variant: {
        outline: '',
        filled: '',
      },
      navigationBackground: {
        black: '',
        white: '',
        lime: '',
        beige: '',
        taupe: '',
      },
      size: {
        default: 'h-[46px] px-6',
      },
    },
    compoundVariants: [
      // Outline variant on light backgrounds
      {
        variant: 'outline',
        navigationBackground: 'white',
        className: 'bg-transparent border border-civo-black text-civo-black hover:bg-civo-black/5',
      },
      {
        variant: 'outline',
        navigationBackground: 'lime',
        className: 'bg-transparent border border-civo-black text-civo-black hover:bg-civo-black/5',
      },
      {
        variant: 'outline',
        navigationBackground: 'beige',
        className: 'bg-transparent border border-civo-black text-civo-black hover:bg-civo-black/5',
      },
      {
        variant: 'outline',
        navigationBackground: 'taupe',
        className: 'bg-transparent border border-civo-black text-civo-black hover:bg-civo-black/5',
      },
      // Outline variant on dark backgrounds
      {
        variant: 'outline',
        navigationBackground: 'black',
        className: 'bg-transparent border border-white text-white hover:bg-white/5',
      },
      // Filled variant on light backgrounds
      {
        variant: 'filled',
        navigationBackground: 'white',
        className: 'bg-civo-black text-white border border-civo-black hover:bg-civo-black/90',
      },
      {
        variant: 'filled',
        navigationBackground: 'lime',
        className: 'bg-civo-black text-white border border-civo-lime hover:bg-civo-black/90',
      },
      {
        variant: 'filled',
        navigationBackground: 'beige',
        className: 'bg-civo-black text-white border border-civo-black hover:bg-civo-black/90',
      },
      {
        variant: 'filled',
        navigationBackground: 'taupe',
        className: 'bg-civo-black text-white border border-civo-black hover:bg-civo-black/90',
      },
      // Filled variant on dark backgrounds
      {
        variant: 'filled',
        navigationBackground: 'black',
        className: 'bg-civo-lime text-civo-black border border-civo-lime hover:bg-[#00e865]',
      },
    ],
    defaultVariants: {
      variant: 'filled',
      navigationBackground: 'white',
      size: 'default',
    },
  },
)

/**
 * NavCTAProps interface
 *
 * @description Props for the NavCTA component, extending standard HTML button attributes
 * with navigation capabilities and visual variant options.
 *
 * @property {React.ReactNode} children - Button text content
 * @property {string} href - Optional URL for navigation when button is clicked
 * @property {boolean} newTab - When true, opens href in new tab with security attributes
 * @property {string} variant - Visual style: 'outline' (bordered) | 'filled' (solid background)
 * @property {string} navigationBackground - Navigation background color for theme adaptation
 * @property {string} size - Size variant, currently only 'default' available
 * @property {string} className - Additional CSS classes for custom styling
 * @property {Function} onClick - Custom click handler, executed before navigation
 */
export interface NavCTAProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof navCTAVariants> {
  children: React.ReactNode
  href?: string
  newTab?: boolean
  className?: string
}

const NavCTA = React.forwardRef<HTMLButtonElement, NavCTAProps>(
  ({ className, variant, navigationBackground, size, children, href, newTab, onClick, ...props }, ref) => {
    const handleClick = (e: React.MouseEvent<HTMLButtonElement>): void => {
      // Call the custom onClick handler first if provided
      if (onClick) {
        onClick(e)
      }

      // Then handle href navigation if provided and event wasn't prevented
      if (href && !e.defaultPrevented) {
        if (newTab) {
          window.open(href, '_blank', 'noopener,noreferrer')
        } else {
          window.location.href = href
        }
      }
    }

    return (
      <button
        className={cn(navCTAVariants({ variant, navigationBackground, size, className }))}
        ref={ref}
        onClick={handleClick}
        {...props}
      >
        <span className="relative block overflow-hidden">
          <span className="block transition-transform duration-300 ease-in-out group-hover:-translate-y-full">
            {children}
          </span>
          <span
            className="absolute left-0 top-0 block translate-y-full transition-transform duration-300 ease-in-out group-hover:translate-y-0"
            aria-hidden="true"
          >
            {children}
          </span>
        </span>
      </button>
    )
  },
)

NavCTA.displayName = 'NavCTA'

export { NavCTA, navCTAVariants }
